import { ChevronRight } from '@mui/icons-material';
import {
  type PlayerRatingAllResponse,
  type PlayerResponse,
  type RecStatusResponse,
  type RpmPercentResponse,
} from '@on3/api';
import { On3IconAlert } from '@on3/icons';
import { Avatar } from '@on3/ui-lib/components/Avatar';
import { Link } from '@on3/ui-lib/components/Link';
import { StarRating } from '@on3/ui-lib/components/Player/StarRating';
import { StarRanking } from '@on3/ui-lib/components/Player/StarRating/Ranking';
import {
  ServiceLogo,
  type ServiceType,
} from '@on3/ui-lib/components/ServiceLogo/ServiceLogo';
import { classYearClaimCutoff } from '@on3/ui-lib/utils/constants';
import { formatCurrency } from '@on3/ui-lib/utils/formatters';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { createUrl } from '@on3/ui-lib/utils/url';
import { type ColumnOption } from '@public/components/Db/Filters/SortFilter';
import { VerifiedBadge } from '@public/components/Elite/VerifiedBadge/VerifiedBadge';
import { StatusContainer } from '@public/components/RecruitmentStatus/StatusContainer/StatusContainer';
import clsx from 'clsx';
import { useRouter } from 'next/router';

import styles from './IndustryComparisonItem.module.scss';

interface IndustryComparisonPlayerItemProps {
  player: PlayerResponse;
  ratings?: PlayerRatingAllResponse[] | null;
  nilValue?: number | null;
  className?: string;
  columns: ColumnOption[];
  selected?: ColumnOption;
  status?: RecStatusResponse;
  predictions?: RpmPercentResponse[] | null;
}
const defaultRatings = [
  { type: 'Industry' },
  { type: 'On3' },
  { type: '247' },
  { type: 'Espn' },
  { type: 'Rivals' },
] as PlayerRatingAllResponse[];

export const IndustryComparisonItem = ({
  player,
  ratings,
  nilValue,
  className,
  columns,
  selected,
  predictions,
  status,
}: IndustryComparisonPlayerItemProps) => {
  const router = useRouter();
  const { year } = router.query;
  const isRivalsIndustry = year && +year > 2025;
  const industryRank = ratings?.[0]?.overallRank;
  const industryRating = ratings?.[0]?.rating?.toFixed(2);
  const playerRatings = ratings?.length ? ratings : defaultRatings;
  const showRank = columns.includes('rank');
  const showJersey = columns.includes('jerseyNumber');
  const playerStatus = status?.shortTermSignee
    ? 'short-term'
    : status?.transferredAsset
      ? 'transfer'
      : slugify(status?.type) || 'none';
  const rankOrJersey = showRank
    ? industryRank
    : showJersey
      ? player.jerseyNumber
      : '-';

  return (
    <li
      className={clsx(styles.block, className)}
      data-rank-jersey={showRank || showJersey}
      data-rivals-industry={isRivalsIndustry}
      data-status={playerStatus}
      key={player.key}
      role="row"
    >
      {(showRank || showJersey) && (
        <span className={styles.rankOrJersey} role="cell">
          {rankOrJersey}
        </span>
      )}

      <figure className={styles.avatar} role="cell">
        <Avatar
          altText={`${player.fullName} Avatar`}
          domain={player.defaultAsset?.domain}
          fit="cover"
          height={100}
          quality={95}
          source={player.defaultAsset?.source}
          type={player.defaultAsset?.type}
          width-={100}
        />
      </figure>
      <div className={styles.details} role="cell">
        <div className={styles.container}>
          <Link
            aria-labelledby="player-name"
            className={styles.name}
            href={createUrl(['rivals', player.slug])}
            useSiteColor
          >
            {player.fullName}
          </Link>

          {status?.shortTermSignee && <On3IconAlert />}
        </div>
        <dl className={styles.vitalsContainer}>
          {player.classYear && (
            <>
              <dt>High School Class Year</dt>
              <dd>H.S. {player.classYear}</dd>
            </>
          )}
          {player.position?.abbr && (
            <>
              <dt>Position Abbreviation</dt>
              <dd>{player.position?.abbr}</dd>
            </>
          )}

          {player.height && (
            <>
              <dt>Height</dt>
              <dd>{player.height}</dd>
            </>
          )}
          {!!player.weight && (
            <>
              <dt>Weight</dt>
              <dd>{player.weight}</dd>
            </>
          )}
          {!!nilValue && (
            <>
              <dt data-type="nil">NIL Value:</dt>
              <dd data-type="nil">{formatCurrency(nilValue)}</dd>
            </>
          )}
        </dl>
        <dl className={styles.homeContainer}>
          {(player.highSchoolName || player.hometown?.abbr) && (
            <>
              <dt>High School / Hometown</dt>
              <dd className={styles.highschoolHometown}>
                <Link
                  className={styles.highschool}
                  href={createUrl(['high-school', player.highSchool?.urlSlug])}
                >
                  {player.highSchoolName}
                </Link>
                {player.hometown?.abbr && <span>({player.hometown.abbr})</span>}
              </dd>
            </>
          )}

          {!!industryRating && (
            <>
              <dt>Industry Rating</dt>
              <dd className={styles.rating}>{industryRating}</dd>
            </>
          )}
        </dl>
      </div>
      <div className={styles.industryRatings}>
        <header
          aria-hidden="true"
          className={styles.industryRankingsHeaders}
          role="presentation"
        >
          <div className={styles.headers}>
            <span>NATL</span>
            <span>POS</span>
            <span>ST</span>
          </div>
        </header>

        <ul
          className={styles.industryRatingsList}
          data-rivals-industry={isRivalsIndustry}
        >
          {playerRatings.map((r, index) => {
            if (isRivalsIndustry && r.type === 'Rivals') {
              return null;
            }

            const isIndustry = r.type === 'Industry';
            const isOn3 = r.type === 'On3';
            const link = isIndustry
              ? createUrl(['rivals', player.slug, 'industry-comparison'])
              : isOn3
                ? createUrl(['rivals', player.slug, 'recruiting'])
                : r.link;

            const labelledBy = isIndustry ? 'on3-industry' : slugify(r.type);
            const logoType =
              isRivalsIndustry && r.type === 'On3' ? 'Rivals' : r.type;

            return (
              <li
                aria-labelledby={labelledBy}
                className={styles.industryItem}
                data-active={selected === r.type}
                data-service-name={r.type}
                key={`${player.key}-${index}`}
                role="cell"
              >
                <div className={styles.serviceLogo}>
                  <ServiceLogo type={logoType as ServiceType} />
                </div>

                <StarRating
                  fiveStarPlus={r.fiveStarPlus && isIndustry}
                  href={link || ''}
                  rating={r.rating}
                  service={r.type?.toLowerCase() as ServiceType}
                  sport={r.sport}
                  stars={r.stars}
                  type={r.rankingType}
                  year={r.rankingYear}
                />
                <StarRanking
                  className={styles.industryRanking}
                  nationalRank={r.overallRank}
                  positionAbbr={r.positionAbbr}
                  positionRank={r.positionRank}
                  returnEmpty
                  sport={r.sport}
                  stateAbbr={r.stateAbbr}
                  stateRank={r.stateRank}
                  useSiteColor={false}
                  year={r.rankingYear}
                />
              </li>
            );
          })}
        </ul>
      </div>
      <div className={styles.statusContainer} role="cell">
        <StatusContainer
          player={player}
          rpm={predictions}
          sport={player.defaultSport}
          status={status}
        />
        <div className={styles.verified}>
          {player.athleteVerified ? (
            <VerifiedBadge />
          ) : (player.classYear || 0) > classYearClaimCutoff ? (
            <Link
              forceReload
              href={createUrl(['rivals', player.slug, 'claim-profile'])}
              useSiteColor
            >
              Claim Profile <ChevronRight />
            </Link>
          ) : null}
        </div>
      </div>
    </li>
  );
};
